import _ from 'lodash';
import { withRouter } from 'react-router-dom';
import React, { useCallback, useContext, useMemo, useState, useEffect } from 'react';
import { ViewModelForm, useDataRefresh } from '@xengage/gw-portals-viewmodel-react';
import { WizardPage } from '@xengage/gw-portals-wizard-react';
import { useValidation } from '@xengage/gw-portals-validation-react';
import { TranslatorContext } from '@jutro/locale';
import { BreakpointTrackerContext } from '@jutro/layout';
import UWCompanyUtil from '../../utils/UWCompanyUtil_TWIA.js';
import metadata from './TaxInformationPage.metatdata.json5';
import styles from './TaxInformationPage.module.scss';
import messages from './TaxInformationPage.messages.js';


function TaxInformationPage(props) {
    const { wizardData :registrationVM, updateWizardData, id,isConfirmationPage} = props;
    
    const breakpoint = useContext(BreakpointTrackerContext)
    const [showErrors, setShowErrors] = useState(false);
    const [readonlyTax, setReadonlyTax] = useState(false);
    const { onValidate, isComponentValid } = useValidation(id);
    const isTWIA = UWCompanyUtil.isTWIACompany(_.get(registrationVM, 'uwcompany'))
    const translator = useContext(TranslatorContext);
    
    function Text() {
        return (<div > {'Refer to specific instructions on W-9 form to enter the tax information.'} <br></br>{'Link to IRS W-9 Form:'}
            <a href="https://www.irs.gov/pub/irs-pdf/fw9.pdf" target="_blank" style={{ color: '#30A6DC', cursor: 'pointer', textDecorationLine: 'none' }}>https://www.irs.gov/pub/irs-pdf/fw9.pdf</a>
        </div>)
    }

    const { refreshData } = useDataRefresh();
    const mailingAddress = _.get(registrationVM,"mailingAddress.value");
    
    useEffect(() => {
        if (registrationVM.hasMailingAddress.value) {
            _.set(registrationVM, "taxAddress.value", mailingAddress)
        }

        updateWizardData(registrationVM);
    }, [registrationVM, mailingAddress]);

    // DE16832 only allow tax edits on clarification for tax detail mismatch
    useEffect(() => {
        if (_.get(registrationVM, 'isAgencyVerification.value')) {
            setReadonlyTax(_.get(registrationVM, 'isTaxDetailsMismatch_TWIA.value') === false);
        }
    }, [registrationVM, setReadonlyTax]);

    const writeValue = useCallback(
        (value, path) => {
            let countHyphen;

            if (path === 'taxInfo.taxID') {
                countHyphen = ((value.match(/-/g) || []).length)
            }

            if (countHyphen === 2) {
                _.set(registrationVM, 'taxInfo.isSSN', true);
                updateWizardData(registrationVM);
            }

            if (countHyphen === 1) {
                _.set(registrationVM, 'taxInfo.isSSN', false);
                updateWizardData(registrationVM);
            }

            _.set(registrationVM, path, value)
            updateWizardData(registrationVM);
            refreshData();
        },
        [refreshData, registrationVM]
    );

    const getUSStateList = useMemo(() => {
        const stateTypelist = registrationVM.taxAddress.state.aspects.availableValues.map((code) => {
            return {
                code: code.code,
                name: translator({ id: code.name })
            }
        })

        return stateTypelist
    }, [registrationVM, translator]);

    /**
     * DE15256 - show error notification on blank TIN
     * @returns 
     */
    const showTINError = () => {
        const taxvalue = _.get(registrationVM, 'taxInfo.taxID.value');    
       return showErrors && (taxvalue === undefined || taxvalue === "");
    };

    const overrideProps = {
        '@field': {
            labelPosition: breakpoint === 'desktop' ? 'left' : 'top',
            showRequired: true,
            readOnly: isConfirmationPage,
            showErrors
        },
        w9Link: {
            content: Text(),
            visible: !isConfirmationPage
        },
        classificationCode: {
            visible: _.get(registrationVM, 'taxInfo.fedTaxClassification.value.code') === 'liability'
        },
        pleaseSpecify: {
            visible: _.get(registrationVM, 'taxInfo.fedTaxClassification.value.code') === 'other'
        },
        address: {
            readOnly: isTWIA || !isTWIA || isConfirmationPage,
            showRequired: true,
            label: translator(messages.address)
        },
        city: {
            readOnly: isTWIA || !isTWIA || isConfirmationPage,
            showRequired: isTWIA ? false : true
        },
        zipCode: {
            readOnly: isTWIA || !isTWIA  || isConfirmationPage,
            showRequired: isTWIA ? false : true
        },
        state: {
            readOnly: isTWIA || !isTWIA || isConfirmationPage,
            showRequired: isTWIA ? false : true,
            availableValues: !isConfirmationPage && getUSStateList,
            value: _.get(registrationVM.value, 'taxAddress.state')
        },
        taxInstructions: {
            visible: isTWIA && !isConfirmationPage,
        },
        taxtfpaInstructions: {
            visible: !isTWIA && !isConfirmationPage,
        },
        addressLine2: {
            visible: !(_.isUndefined(_.get(registrationVM.value,"taxAddress.addressLine2"))),
            readOnly: true
        },
        addressLine3: {
            visible: !(_.isUndefined(_.get(registrationVM.value,"taxAddress.addressLine3"))),
            readOnly: true
        },
        SSN: {
            visible: _.isUndefined(_.get(registrationVM, 'taxInfo.taxID.value')) || (!_.isUndefined(_.get(registrationVM, 'taxInfo.taxID.value')) && (_.get(registrationVM, 'taxInfo.isSSN.value')) === true) || _.get(registrationVM, 'taxInfo.taxID.value') == "",
            readOnly: readonlyTax,
            showErrors: !showTINError() && showErrors
        },
        EIN: {
            visible: _.isUndefined(_.get(registrationVM, 'taxInfo.taxID.value')) || (!_.isUndefined(_.get(registrationVM, 'taxInfo.taxID.value')) && (_.get(registrationVM, 'taxInfo.isSSN.value')) === false) || _.get(registrationVM, 'taxInfo.taxID.value') == "",
            readOnly: readonlyTax,
            showErrors: !showTINError() && showErrors
        },
        tinNotification:{
            visible: showTINError()
        },
        taxInformationLegal: {
            visible: !isConfirmationPage

        },
        twia1099Address: {
            visible: !isConfirmationPage
        },
        taxName: { 
            readOnly: readonlyTax
        },
        businessName: { 
            readOnly: readonlyTax
        },
        federalTax: { 
            readOnly: readonlyTax
        },
        exemptPayee: { 
            readOnly: readonlyTax
        },
        exemptionsFATCA: { 
            readOnly: readonlyTax
        },
        confirmTIN: { 
            readOnly: readonlyTax
        },
        confirmBackupWithholding: { 
            readOnly: readonlyTax
        },
        confirmCitizenship: { 
            readOnly: readonlyTax
        },
        confirmFacta: { 
            readOnly: readonlyTax
        },
    };
    const resolvers = {
        resolveClassNameMap: styles
    };
    const onNext = useCallback(async () => {
        if (!isComponentValid) {
            setShowErrors(true)
            return false
        }
        return registrationVM
    }, [isComponentValid, registrationVM])
    
    return (
        isConfirmationPage ? 
        <ViewModelForm
                uiProps={metadata.pageContent}
                model={registrationVM}
                overrideProps={overrideProps}
                onModelChange={updateWizardData}
                onValidationChange={onValidate}
                onValueChange={writeValue}
                callbackMap={resolvers.resolveCallbackMap}
                componentMap={resolvers.resolveComponentMap}
                classNameMap={resolvers.resolveClassNameMap}
            /> :
        <WizardPage
            id={id}
            onNext={onNext}
        >
            <ViewModelForm
                uiProps={metadata.pageContent}
                model={registrationVM}
                overrideProps={overrideProps}
                onModelChange={updateWizardData}
                onValidationChange={onValidate}
                onValueChange={writeValue}
                callbackMap={resolvers.resolveCallbackMap}
                componentMap={resolvers.resolveComponentMap}
                classNameMap={resolvers.resolveClassNameMap}
            />
        </WizardPage>
    );
}

export default withRouter(TaxInformationPage);
